import React, { Fragment } from 'react'
import { NextRouter, useRouter } from 'next/router'
import i18nextConfig from '../../../next-i18next.config'
import {
  Transition,
  Menu,
  MenuItems,
  MenuItem,
  MenuButton,
} from '@headlessui/react'
import Link from './Link'
import { getCleanPath } from '../../lib/getCleanPath'
import {
  languageFlagMapping,
  languageLabelMapping,
} from '../../constants/languageMapping'
import useLocale from '../../hooks/useLocale'
import { HeroIcons } from '../../lib/utils/heroIcons'
import gtmService from '../../services/gtm.service'

interface LanguageSelectProps {
  mode?: 'dropdown' | 'toggle'
}

export default function LanguageSelect({
  mode = 'dropdown',
}: LanguageSelectProps) {
  const router: NextRouter = useRouter()
  const { locale, setLocale } = useLocale()

  const supportedLngs = i18nextConfig.i18n.locales
  const alternateLocale =
    supportedLngs.find((language) => language !== locale) || locale

  const onLanguageChange = async (newLocale: any) => {
    gtmService.contentEngagement({
      contentType: 'language',
      contentId: newLocale,
      interactionType: 'language_change',
    })
    await setLocale(newLocale)
  }

  if (mode === 'toggle') {
    return (
      <Link
        href={getCleanPath(router.asPath)}
        locale={alternateLocale}
        onClick={() => onLanguageChange(alternateLocale)}
        className="inline-flex items-center justify-center rounded-button p-1 transition-transform duration-200 hover:scale-110"
        aria-label={`Switch to ${languageLabelMapping[alternateLocale]}`}
        title={languageLabelMapping[alternateLocale]}
      >
        <span aria-hidden className="text-[1.45rem] leading-none">
          {languageFlagMapping[alternateLocale] || '🌐'}
        </span>
        <span className="sr-only">{languageLabelMapping[alternateLocale]}</span>
      </Link>
    )
  }

  return (
    <div className="cursor-pointer">
      <Menu>
        <div className="relative flex items-center">
          <MenuButton className="relative flex h-10 w-10 cursor-default items-center justify-center rounded-md text-current hover:cursor-pointer hover:bg-sheltersBlue/10">
            <div className="pt-0.5">
              <HeroIcons.GlobeAltIcon
                aria-hidden="true"
                className="h-5 w-5 text-current"
              />
            </div>
            <span className="sr-only">{languageLabelMapping[locale]}</span>
          </MenuButton>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <MenuItems className="absolute right-0 top-10 mt-1 max-h-60 w-36 rounded-inner-card bg-white py-1 text-sm text-[#293254] shadow-lg">
              {supportedLngs.map((lng) => (
                <MenuItem key={lng} as={Fragment}>
                  <Link
                    href={getCleanPath(router.asPath)}
                    locale={lng}
                    onClick={() => onLanguageChange(lng)}
                    className="flex px-3 py-1"
                  >
                    <span className="pr-3 text-sheltersDarkBlue">
                      {languageFlagMapping[lng]}
                    </span>
                    <span
                      className={`block ${
                        locale === lng ? 'font-medium' : 'font-normal'
                      }`}
                    >
                      {languageLabelMapping[lng]}
                    </span>
                  </Link>
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </div>
      </Menu>
    </div>
  )
}
